import { useState, useEffect, useCallback } from 'react'
import { ethers } from 'ethers'

const POLYGON_CHAIN_ID = 137
const POLYGON_RPC = 'https://polygon-rpc.com'
const USDC_CONTRACT = '0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174'

const USDC_ABI = [
  'function balanceOf(address owner) view returns (uint256)',
  'function decimals() view returns (uint8)',
]

interface WalletState {
  isConnected: boolean
  address: string | null
  balance: string
  chainId: number | null
  provider: ethers.BrowserProvider | null
}

export function useWallet() {
  const [state, setState] = useState<WalletState>({
    isConnected: false,
    address: null,
    balance: '0',
    chainId: null,
    provider: null,
  })

  const updateBalance = useCallback(async (address: string, provider: ethers.Provider) => {
    try {
      const usdcContract = new ethers.Contract(USDC_CONTRACT, USDC_ABI, provider)
      const balance = await usdcContract.balanceOf(address)
      const decimals = await usdcContract.decimals()
      const formattedBalance = ethers.formatUnits(balance, decimals)
      setState(prev => ({ ...prev, balance: formattedBalance }))
    } catch (error) {
      console.error('Failed to fetch USDC balance:', error)
      setState(prev => ({ ...prev, balance: '0' }))
    }
  }, [])

  const connect = useCallback(async () => {
    if (!window.ethereum) {
      alert('Please install MetaMask or another Web3 wallet')
      return
    }

    try {
      const provider = new ethers.BrowserProvider(window.ethereum, {
        name: 'Polygon',
        chainId: POLYGON_CHAIN_ID,
      })

      // Request account access
      await provider.send('eth_requestAccounts', [])

      const signer = await provider.getSigner()
      const address = await signer.getAddress()
      const network = await provider.getNetwork()
      const chainId = Number(network.chainId)

      // Switch to Polygon if not already on it
      if (chainId !== POLYGON_CHAIN_ID) {
        try {
          await window.ethereum.request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: '0x89' }], // 137 in hex
          })
        } catch (switchError: any) {
          // If Polygon isn't added, add it
          if (switchError.code === 4902) {
            await window.ethereum.request({
              method: 'wallet_addEthereumChain',
              params: [
                {
                  chainId: '0x89',
                  chainName: 'Polygon Mainnet',
                  nativeCurrency: {
                    name: 'MATIC',
                    symbol: 'MATIC',
                    decimals: 18,
                  },
                  rpcUrls: ['https://polygon-rpc.com'],
                  blockExplorerUrls: ['https://polygonscan.com'],
                },
              ],
            })
          } else {
            throw switchError
          }
        }
      }

      setState({
        isConnected: true,
        address,
        balance: '0',
        chainId: POLYGON_CHAIN_ID,
        provider,
      })

      // Fetch USDC balance
      await updateBalance(address, provider)
    } catch (error: any) {
      console.error('Failed to connect wallet:', error)
      alert(error.message || 'Failed to connect wallet')
    }
  }, [updateBalance])

  const disconnect = useCallback(() => {
    setState({
      isConnected: false,
      address: null,
      balance: '0',
      chainId: null,
      provider: null,
    })
  }, [])

  // Listen for account changes
  useEffect(() => {
    if (window.ethereum) {
      const handleAccountsChanged = (accounts: string[]) => {
        if (accounts.length === 0) {
          disconnect()
        } else if (accounts[0] !== state.address) {
          connect()
        }
      }

      const handleChainChanged = () => {
        window.location.reload()
      }

      window.ethereum.on('accountsChanged', handleAccountsChanged)
      window.ethereum.on('chainChanged', handleChainChanged)

      return () => {
        window.ethereum.removeListener('accountsChanged', handleAccountsChanged)
        window.ethereum.removeListener('chainChanged', handleChainChanged)
      }
    }
  }, [state.address, connect, disconnect])

  return {
    ...state,
    connect,
    disconnect,
    refreshBalance: state.address && state.provider 
      ? () => updateBalance(state.address!, state.provider!)
      : undefined,
  }
}
